import type { TFormAnswer } from 'types/textVisit';

/** Matches text visit and video appointment `formGroups` entries */
export type TFormGroupLike = {
    answers?: TFormAnswer[];
    formType?: string;
};

export type TIntakeAssessmentNavItem = {
    name: string;
    value: number;
    max: number;
    getLevel: (num: number) => number;
};

export type TParseSpecificIntakeFormResult = {
    navItems: TIntakeAssessmentNavItem[];
    groupedIntakeAnswers: Record<string, TFormAnswer[]>;
    commonIntakeAnswers: TFormAnswer[];
};

/** Normalizes `formType` so casing/spacing variants still match `SPECIFIC_INTAKE_FORM`. */
export function isSpecificIntakeFormType(formType: string | undefined): boolean {
    return (formType ?? '').toUpperCase().replace(/\s+/g, '_') === 'SPECIFIC_INTAKE_FORM';
}

/** All answers from every `SPECIFIC_INTAKE_FORM` group, in `formGroups` order. */
export function collectSpecificIntakeAnswers(formGroups: TFormGroupLike[] | undefined | null): TFormAnswer[] {
    if (!formGroups?.length) return [];
    return formGroups.filter((g) => isSpecificIntakeFormType(g.formType)).flatMap((g) => g.answers ?? []);
}

/**
 * Mirrors IntakeFormDrawer IntakeFormContent: groups SPECIFIC_INTAKE_FORM answers by section
 * and builds score summary rows (e.g. ASRS / GAD-7 / PHQ-9) when a section has 2+ questions.
 */
export function parseSpecificIntakeFormGroups(
    formGroups: TFormGroupLike[] | undefined | null,
): TParseSpecificIntakeFormResult {
    const empty: TParseSpecificIntakeFormResult = {
        navItems: [],
        groupedIntakeAnswers: {},
        commonIntakeAnswers: [],
    };
    if (!formGroups?.length) {
        return empty;
    }

    const specificAnswers = collectSpecificIntakeAnswers(formGroups);
    if (!specificAnswers.length) {
        return empty;
    }

    const groupedIntakeAnswers: Record<string, TFormAnswer[]> = {};
    const commonIntakeAnswers: TFormAnswer[] = [];

    specificAnswers.forEach((answer) => {
        if (!answer.sectionName) {
            commonIntakeAnswers.push(answer);
        } else {
            if (!groupedIntakeAnswers[answer.sectionName]) {
                groupedIntakeAnswers[answer.sectionName] = [];
            }
            groupedIntakeAnswers[answer.sectionName].push(answer);
        }
    });

    Object.keys(groupedIntakeAnswers).forEach((key) => {
        const answers = groupedIntakeAnswers[key];
        if (answers.length === 1) {
            commonIntakeAnswers.push(answers[0]);
            delete groupedIntakeAnswers[key];
        }
    });

    const navItems: TIntakeAssessmentNavItem[] = [];
    Object.keys(groupedIntakeAnswers).forEach((key) => {
        const answers = groupedIntakeAnswers[key];
        let max = 0;
        let value = 0;
        answers.forEach((answer) => {
            value += answer.score || 0;
            max += answer.totalScore || 0;
        });
        navItems.push({
            name: key,
            value,
            max,
            getLevel: (num: number) => {
                const levelScore = max / 4;
                return Math.ceil(num / levelScore);
            },
        });
    });

    return { navItems, groupedIntakeAnswers, commonIntakeAnswers };
}
